<template>
    <div class="sp-paso">
        <header class="sp-paso__head">
            <span class="sp-icon-badge"><i class="pi pi-building" aria-hidden="true"></i></span>
            <div>
                <h2 class="sp-h3">Cuéntanos sobre tu negocio</h2>
                <p>Registra los datos principales del establecimiento que quieres promocionar.</p>
            </div>
        </header>

        <fieldset class="sp-tipo-persona">
            <legend>Tipo de persona <span class="sp-campo__req" aria-hidden="true">*</span></legend>
            <div class="sp-tipo-persona__opciones">
                <label
                    v-for="opcion in tiposPersona"
                    :key="opcion.valor"
                    class="sp-tipo-persona__opcion"
                    :class="{ 'is-activa': datos.tipo_persona === opcion.valor }"
                >
                    <RadioButton
                        v-model="datos.tipo_persona"
                        :inputId="'sp-tp-' + opcion.valor"
                        name="tipo_persona"
                        :value="opcion.valor"
                    />
                    <span>
                        <i :class="'pi ' + opcion.icono" aria-hidden="true"></i>
                        {{ opcion.etiqueta }}
                    </span>
                </label>
            </div>
        </fieldset>

        <div class="sp-campo">
            <label :for="id('razon')">
                <span>{{ esJuridica ? 'Razón social' : 'Nombre del titular' }}</span>
                <span class="sp-campo__req" aria-hidden="true">*</span>
            </label>
            <InputText
                :id="id('razon')"
                v-model.trim="datos.razon_social"
                :class="{ 'p-invalid': error('razon_social') }"
            />
            <p v-if="error('razon_social')" class="sp-campo__error">
                <i class="pi pi-exclamation-circle" aria-hidden="true"></i><span>{{ error('razon_social') }}</span>
            </p>
            <p v-else-if="!esJuridica" class="sp-campo__ayuda">
                Prellenado con el titular registrado en el paso anterior. Puedes ajustarlo.
            </p>
        </div>

        <div v-if="esJuridica" class="sp-form__row sp-form__row--nit">
            <div class="sp-campo">
                <label :for="id('nit')">
                    <span>NIT</span><span class="sp-campo__req" aria-hidden="true">*</span>
                </label>
                <InputText
                    :id="id('nit')"
                    v-model.trim="datos.nit"
                    inputmode="numeric"
                    placeholder="Ej. 900123456"
                    :class="{ 'p-invalid': error('nit') }"
                />
                <p v-if="error('nit')" class="sp-campo__error">
                    <i class="pi pi-exclamation-circle" aria-hidden="true"></i><span>{{ error('nit') }}</span>
                </p>
            </div>

            <div class="sp-campo">
                <label :for="id('dv')"><span>Dígito de verificación</span></label>
                <InputText
                    :id="id('dv')"
                    v-model.trim="datos.digito_verificacion"
                    inputmode="numeric"
                    maxlength="2"
                    placeholder="Ej. 1"
                />
            </div>
        </div>

        <div class="sp-form__row">
            <div class="sp-campo">
                <label :for="id('comercial')"><span>Nombre comercial</span></label>
                <InputText
                    :id="id('comercial')"
                    v-model.trim="datos.nombre_comercial"
                    placeholder="Ej. Restaurante Doña María"
                />
            </div>

            <div class="sp-campo">
                <label :for="id('telefono')"><span>Teléfono del negocio</span></label>
                <InputMask :id="id('telefono')" v-model="datos.telefono" mask="999 999 9999" placeholder="300 987 6543" />
            </div>
        </div>

        <div class="sp-campo">
            <label :for="id('categoria')">
                <span>Categoría del negocio</span><span class="sp-campo__req" aria-hidden="true">*</span>
            </label>
            <Dropdown
                :id="id('categoria')"
                v-model="datos.id_categoria_negocio"
                :options="categorias"
                optionLabel="nombre"
                optionValue="id"
                placeholder="Selecciona la categoría"
                :loading="cargandoCatalogos"
                :class="{ 'p-invalid': error('id_categoria_negocio') }"
            />
            <p v-if="error('id_categoria_negocio')" class="sp-campo__error">
                <i class="pi pi-exclamation-circle" aria-hidden="true"></i><span>{{ error('id_categoria_negocio') }}</span>
            </p>
        </div>

        <div class="sp-campo">
            <label :for="id('direccion')">
                <span>Dirección del negocio</span><span class="sp-campo__req" aria-hidden="true">*</span>
            </label>
            <InputText
                :id="id('direccion')"
                v-model.trim="datos.direccion"
                placeholder="Calle 123 # 45-67"
                :class="{ 'p-invalid': error('direccion') }"
            />
            <p v-if="error('direccion')" class="sp-campo__error">
                <i class="pi pi-exclamation-circle" aria-hidden="true"></i><span>{{ error('direccion') }}</span>
            </p>
        </div>

        <div class="sp-form__row">
            <div class="sp-campo">
                <label :for="id('depto')">
                    <span>Departamento</span><span class="sp-campo__req" aria-hidden="true">*</span>
                </label>
                <Dropdown
                    :id="id('depto')"
                    v-model="datos.departamento"
                    :options="departamentos"
                    optionLabel="label"
                    optionValue="value"
                    placeholder="Selecciona el departamento"
                    :filter="true"
                    filterPlaceholder="Buscar…"
                    :loading="cargandoCatalogos"
                    :class="{ 'p-invalid': error('departamento') }"
                    @change="alCambiarDepartamento"
                />
                <p v-if="error('departamento')" class="sp-campo__error">
                    <i class="pi pi-exclamation-circle" aria-hidden="true"></i><span>{{ error('departamento') }}</span>
                </p>
            </div>

            <div class="sp-campo">
                <label :for="id('municipio')">
                    <span>Ciudad / municipio</span><span class="sp-campo__req" aria-hidden="true">*</span>
                </label>
                <Dropdown
                    :id="id('municipio')"
                    v-model="datos.id_divipola"
                    :options="municipios"
                    optionLabel="label"
                    optionValue="value"
                    :placeholder="datos.departamento ? 'Selecciona la ciudad' : 'Elige primero el departamento'"
                    :disabled="!datos.departamento"
                    :filter="true"
                    filterPlaceholder="Buscar…"
                    :class="{ 'p-invalid': error('id_divipola') }"
                />
                <p v-if="error('id_divipola')" class="sp-campo__error">
                    <i class="pi pi-exclamation-circle" aria-hidden="true"></i><span>{{ error('id_divipola') }}</span>
                </p>
            </div>
        </div>

        <!-- Aclaración obligatoria: el comercio NO define su área de cobertura -->
        <p class="sp-nota">
            <i class="pi pi-info-circle" aria-hidden="true"></i>
            <span>
                Usamos la ubicación de tu sede para que Seway defina después el área de cobertura
                desde la que tus ofertas se mostrarán a los conductores.
            </span>
        </p>
    </div>
</template>

<script>
import { departamentosDe, municipiosDe } from '../catalogos';

export default {
    name: 'PasoNegocio',
    props: {
        datos: { type: Object, required: true },
        errores: { type: Object, default: () => ({}) },
        categorias: { type: Array, default: () => [] },
        divipola: { type: Array, default: () => [] },
        cargandoCatalogos: { type: Boolean, default: false }
    },
    data() {
        return {
            tiposPersona: [
                { valor: 'JURIDICA', etiqueta: 'Persona jurídica', icono: 'pi-building' },
                { valor: 'NATURAL', etiqueta: 'Persona natural', icono: 'pi-user' }
            ]
        };
    },
    computed: {
        esJuridica() {
            return this.datos.tipo_persona === 'JURIDICA';
        },
        departamentos() {
            return departamentosDe(this.divipola);
        },
        municipios() {
            return municipiosDe(this.divipola, this.datos.departamento);
        }
    },
    methods: {
        id(campo) {
            return 'sp-negocio-' + campo;
        },
        error(campo) {
            return this.errores[campo] || '';
        },
        alCambiarDepartamento() {
            // Cambiar de departamento invalida el municipio seleccionado.
            this.datos.id_divipola = null;
        }
    }
};
</script>

<style lang="scss" scoped>
.sp-paso {
    display: flex;
    flex-direction: column;
    gap: 1.25rem;
}

.sp-paso__head {
    display: flex;
    align-items: center;
    gap: 1rem;

    p {
        margin-top: 0.25rem;
        color: var(--sp-text-soft);
        font-size: 0.92rem;
    }
}

.sp-tipo-persona {
    margin: 0;
    padding: 0;
    border: 0;

    legend {
        display: flex;
        align-items: center;
        gap: 0.25rem;
        padding: 0;
        margin-bottom: 0.6rem;
        color: var(--sp-navy);
        font-size: 0.9rem;
        font-weight: 700;
    }
}

.sp-tipo-persona__opciones {
    display: grid;
    grid-template-columns: 1fr;
    gap: 0.75rem;
}

.sp-tipo-persona__opcion {
    display: flex;
    align-items: center;
    gap: 0.7rem;
    padding: 0.85rem 1rem;
    background: var(--sp-surface);
    border: 1.5px solid var(--sp-border);
    border-radius: 12px;
    cursor: pointer;
    transition: border-color 0.2s ease, background 0.2s ease;

    span {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        color: var(--sp-navy);
        font-size: 0.92rem;
        font-weight: 600;
    }

    i {
        color: var(--sp-orange);
    }

    &.is-activa {
        border-color: var(--sp-orange);
        background: var(--sp-orange-50);
    }
}

.sp-campo__ayuda {
    color: var(--sp-text-soft);
    font-size: 0.78rem;
}

.sp-nota {
    display: flex;
    align-items: flex-start;
    gap: 0.7rem;
    padding: 0.9rem 1.1rem;
    background: var(--sp-orange-50);
    border-left: 4px solid var(--sp-orange);
    border-radius: 0 10px 10px 0;
    color: var(--sp-navy);
    font-size: 0.85rem;

    i {
        flex: 0 0 auto;
        margin-top: 0.15rem;
        color: var(--sp-orange);
    }
}

@media (min-width: 576px) {
    .sp-tipo-persona__opciones {
        grid-template-columns: 1fr 1fr;
    }

    .sp-form__row--nit {
        grid-template-columns: 2fr 1fr;
    }
}
</style>
